import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { SuperAdminToolsModal, SuperAdminToolsTab } from '../Partials/SuperAdminTools';

interface SuperAdminToolsModalControls {
  openSuperAdminTools: (tab?: SuperAdminToolsTab) => void;
}

const SuperAdminToolsModalContext = createContext<SuperAdminToolsModalControls | undefined>(undefined);

/**
 * Hook to open the super-admin tools modal (current org, server health, create org, batch upload, bulk export) from anywhere in the app.
 *
 * The modal is mounted once at the root, so it opens over the current page without navigating away. It first
 * opens on Current Org, then reopens on the last tab used. Non-super-admins get a no-op: the modal renders nothing for them, so callers
 * never need to gate on the role themselves.
 *
 * @example
 * ```tsx
 * const { openSuperAdminTools } = useSuperAdminToolsModal();
 * <button onClick={() => openSuperAdminTools('health')}>Server Health</button>
 * ```
 */
export const useSuperAdminToolsModal = (): SuperAdminToolsModalControls => {
  const context = useContext(SuperAdminToolsModalContext);
  if (!context) {
    throw new Error('useSuperAdminToolsModal must be used within a SuperAdminToolsModalProvider');
  }
  return context;
};

export const SuperAdminToolsModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<SuperAdminToolsTab>('currentOrg');

  const openSuperAdminTools = useCallback((nextTab?: SuperAdminToolsTab) => {
    if (nextTab) setTab(nextTab);
    setIsOpen(true);
  }, []);
  const controls = useMemo(() => ({ openSuperAdminTools }), [openSuperAdminTools]);

  return (
    <SuperAdminToolsModalContext.Provider value={controls}>
      {children}
      <SuperAdminToolsModal visible={isOpen} tab={tab} onTabChange={setTab} onClose={() => setIsOpen(false)} />
    </SuperAdminToolsModalContext.Provider>
  );
};
